import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { MfaAuthorizationCodeSchema, type RestoreSafetyState } from "@vekha/contracts";
import { Button, Field, Notice, PageHeader } from "../components/ui";
import { useAuth } from "../lib/auth-context";
import {
  beginMfaEnrollment, fetchRestoreSafety, reconcileRestoreSafety,
  verifyMfaEnrollment, type MfaEnrollmentGrant,
} from "../lib/api";

export function canReconcileRestore(state: RestoreSafetyState | null): boolean {
  return Boolean(state?.holdActive && state.operationId && state.blockers.length === 0);
}

// Re-read immediately before mutation: never substitute a new operation for the one reviewed.
export async function reconcileReviewedRestore(
  reviewed: RestoreSafetyState,
  confirmation: { reason: string; currentPassword: string; mfaCode: string },
  csrfToken: string,
  read = fetchRestoreSafety,
  reconcile = reconcileRestoreSafety,
): Promise<RestoreSafetyState> {
  if (!canReconcileRestore(reviewed)) throw new Error("RESTORE_NOT_READY");
  const current = await read();
  if (!canReconcileRestore(current) || current.operationId !== reviewed.operationId) {
    throw new Error("RESTORE_CHANGED");
  }
  return reconcile({ ...confirmation, operationId: current.operationId! }, csrfToken);
}

export function RecoveryPage() {
  const { session } = useAuth();
  const permitted = session?.actor.role === "owner" || session?.actor.role === "admin";
  return (
    <main className="owner-recovery">
      <Link to="/dialogs">← Вернуться в Веху</Link>
      <PageHeader title="Восстановление" description="Второй фактор и сверка восстановленной базы." />
      {permitted ? <OwnerRecovery key={session.actor.userId} /> : (
        <Notice tone="warning" title="Доступно только владельцу и администратору" />
      )}
    </main>
  );
}

function OwnerRecovery() {
  const { session, markMfaEnrolled } = useAuth();
  const [safety, setSafety] = useState<RestoreSafetyState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [grant, setGrant] = useState<MfaEnrollmentGrant | null>(null);
  const [password, setPassword] = useState("");
  const [currentCode, setCurrentCode] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [savedCodes, setSavedCodes] = useState(false);
  const [reason, setReason] = useState("");
  const [restorePassword, setRestorePassword] = useState("");
  const [restoreCode, setRestoreCode] = useState("");

  useEffect(() => {
    let active = true;
    void fetchRestoreSafety().then((state) => {
      if (active) setSafety(state);
    }, () => {
      if (active) setError("Состояние восстановления не подтверждено. Повторите проверку.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  async function refreshSafety() {
    setLoading(true);
    setSafety(null);
    setError(null);
    setNotice(null);
    setRestorePassword("");
    setRestoreCode("");
    try { setSafety(await fetchRestoreSafety()); }
    catch { setError("Состояние восстановления не подтверждено. Повторите проверку."); }
    finally { setLoading(false); }
  }

  async function enroll(event: FormEvent) {
    event.preventDefault();
    if (!session || busy || !password) return;
    setBusy(true); setError(null); setNotice(null); setGrant(null); setSavedCodes(false);
    const enteredPassword = password;
    const enteredCode = currentCode;
    setPassword(""); setCurrentCode("");
    try { setGrant(await beginMfaEnrollment(enteredPassword, session.csrfToken, enteredCode)); }
    catch { setError("Не удалось начать настройку MFA. Проверьте пароль и текущий код, затем повторите."); }
    finally { setBusy(false); }
  }

  async function verify(event: FormEvent) {
    event.preventDefault();
    if (!session || busy || !grant || !savedCodes || !/^\d{6}$/u.test(verifyCode)) return;
    setBusy(true); setError(null);
    const enteredCode = verifyCode;
    setVerifyCode("");
    try {
      await verifyMfaEnrollment(enteredCode, session.csrfToken);
      markMfaEnrolled(); setGrant(null); setSavedCodes(false);
      setNotice("MFA подключён. Для сверки базы используйте новый код приложения.");
    } catch { setError("Код не подтверждён. Введите новый шестизначный код приложения."); }
    finally { setBusy(false); }
  }

  async function reconcile(event: FormEvent) {
    event.preventDefault();
    if (!session?.mfaEnrolled || busy || loading || !safety || !canReconcileRestore(safety)) return;
    setBusy(true); setError(null); setNotice(null);
    const confirmation = { reason: reason.trim(), currentPassword: restorePassword, mfaCode: restoreCode.trim() };
    setRestorePassword(""); setRestoreCode("");
    try {
      const result = await reconcileReviewedRestore(safety, confirmation, session.csrfToken);
      setSafety(result);
      if (!result.holdActive && result.operationId === safety.operationId) {
        setNotice("Сверка завершена. Отправки и сценарии автоматически не включаются.");
        setReason("");
      } else {
        setError("Сервер не подтвердил завершение этой операции. Проверьте актуальное состояние.");
      }
    } catch {
      setSafety(null);
      setError("Сверка не подтверждена. Обновите состояние: операция или блокировки могли измениться. Проверьте пароль и MFA перед повтором.");
    } finally { setBusy(false); }
  }

  return <>
    {error ? <Notice tone="danger" title={error} /> : null}
    {notice ? <Notice tone="safe" title={notice} /> : null}
    <section className="owner-recovery__section" aria-labelledby="mfa-title">
      <h2 id="mfa-title">Второй фактор (MFA)</h2>
      <p>{session?.mfaEnrolled ? "MFA подключён. При необходимости можно заменить приложение." : "Подключите приложение-аутентификатор перед сверкой базы."}</p>
      {!grant ? <form onSubmit={(event) => void enroll(event)} autoComplete="off">
        <fieldset disabled={busy}>
          <Field label="Текущий пароль" htmlFor="mfa-password"><input id="mfa-password" type="password" autoComplete="off" value={password} onChange={(event) => setPassword(event.target.value)} required /></Field>
          {session?.mfaEnrolled ? <Field label="Текущий код MFA или резервный код" htmlFor="mfa-current-code"><input id="mfa-current-code" type="password" autoComplete="off" value={currentCode} onChange={(event) => setCurrentCode(event.target.value)} required /></Field> : null}
          <Button type="submit" disabled={!password || (session?.mfaEnrolled && !MfaAuthorizationCodeSchema.safeParse(currentCode).success)}>{session?.mfaEnrolled ? "Заменить MFA" : "Настроить MFA"}</Button>
        </fieldset>
      </form> : <form onSubmit={(event) => void verify(event)} autoComplete="off">
        <fieldset disabled={busy}>
          <p>Добавьте ключ в приложение-аутентификатор вручную. Сохраните резервные коды в своём менеджере паролей: после подтверждения они будут скрыты.</p>
          <div className="mfa-secret"><div><strong>Ключ приложения</strong><code>{grant.secret}</code></div></div>
          <div className="recovery-codes" aria-label="Резервные коды">{grant.recoveryCodes.map((code) => <code key={code}>{code}</code>)}</div>
          <label className="owner-recovery__check"><input type="checkbox" checked={savedCodes} onChange={(event) => setSavedCodes(event.target.checked)} /> Резервные коды сохранены</label>
          <Field label="Шестизначный код нового приложения" htmlFor="mfa-verify-code"><input id="mfa-verify-code" type="password" inputMode="numeric" maxLength={6} autoComplete="off" value={verifyCode} onChange={(event) => setVerifyCode(event.target.value)} required /></Field>
          <Button type="submit" disabled={!savedCodes || !/^\d{6}$/u.test(verifyCode)}>Подтвердить MFA</Button>
          <Button variant="quiet" onClick={() => { setGrant(null); setVerifyCode(""); setSavedCodes(false); }}>Скрыть ключ и коды</Button>
        </fieldset>
      </form>}
    </section>
    <section className="owner-recovery__section" aria-labelledby="restore-title">
      <h2 id="restore-title">Сверка восстановленной базы</h2>
      <Button variant="secondary" disabled={loading || busy} onClick={() => void refreshSafety()}>Обновить состояние</Button>
      {loading ? <p role="status">Проверяем состояние…</p> : safety ? <>
        <Notice tone={safety.holdActive ? "warning" : "safe"} title={safety.holdActive ? "После восстановления действует блокировка" : "Блокировки восстановления нет"} />
        <dl><dt>Операция восстановления</dt><dd>{safety.operationId ?? "Нет операции"}</dd><dt>База восстановлена</dt><dd>{safety.restoredAt ?? "Нет данных"}</dd><dt>Отправки с неизвестным результатом</dt><dd>{safety.ambiguousEffectCount}</dd></dl>
        {safety.ambiguousEffectCount > 0 ? <p>Отправки с неизвестным результатом остаются в карантине и не будут повторены этой сверкой.</p> : null}
        {safety.blockers.length > 0 ? <ul>{safety.blockers.map((blocker, index) => <li key={index}><strong>{blocker.label}:</strong> {blocker.reason}</li>)}</ul> : null}
        {safety.holdActive && !safety.operationId ? <Notice tone="danger" title="Сервер не вернул операцию восстановления. Подтверждение недоступно." /> : null}
      </> : <Notice tone="warning" title="Состояние неизвестно — сверка недоступна" />}
      <p>Сверка снимает только блокировку восстановления. Она не включает отправку, не запускает сценарии и не повторяет команды провайдерам.</p>
      <form onSubmit={(event) => void reconcile(event)} autoComplete="off">
        <fieldset disabled={busy || loading || !session?.mfaEnrolled || !canReconcileRestore(safety) || grant !== null}>
          <Field label="Основание сверки" htmlFor="restore-reason" hint="Опишите, что проверено после восстановления (не менее 10 символов)."><textarea id="restore-reason" minLength={10} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} required /></Field>
          <Field label="Пароль для сверки" htmlFor="restore-password"><input id="restore-password" type="password" autoComplete="off" value={restorePassword} onChange={(event) => setRestorePassword(event.target.value)} required /></Field>
          <Field label="Код MFA или резервный код для сверки" htmlFor="restore-code"><input id="restore-code" type="password" autoComplete="off" value={restoreCode} onChange={(event) => setRestoreCode(event.target.value)} required /></Field>
          <Button type="submit" disabled={reason.trim().length < 10 || !restorePassword || !MfaAuthorizationCodeSchema.safeParse(restoreCode).success}>Подтвердить сверку базы</Button>
        </fieldset>
      </form>
    </section>
    {safety?.holdActive === false ? <section className="owner-recovery__section">
      <h2>Вернуться к работе</h2>
      <p>Выберите нужный режим в сценариях: «Черновики» или «Автопилот». Отдельно включать доставку и повторно вводить пароль не нужно.</p>
      <Link to="/scenarios">Перейти к сценариям</Link>
    </section> : null}
  </>;
}
